import { computed } from 'vue';
import { useProduct } from '~/modules/product/composables/useProduct';
import { storeToRefs } from 'pinia';
import { useProductType } from '~/composables/useProductType';
import useUpc from '~/modules/upc/composables/useUpc';
import useSelected2ndVariant from '~/modules/third-variant/composables/useSelected2ndVariant';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { getMemberDisplayGroupsMap } from '~/utils/product';
import { useStickifiedContextData } from '~/modules/sticky-product-traits/composables/useStickifiedContextData';

/**
 *
 * @returns {import('vue').ComputedRef<string>}
 */
export default function useSelectedMemberId() {
  const { productData: product, productId } = useProduct();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const { isConsolidatedPage } = useProductType();

  if (isShopTheSetExpEnabled) {
    const { getStickifiedContextName } = useStickifiedContextData();
    const consolidatedStore = useConsolidatedMemberStore();
    const { selectedMember } = storeToRefs(consolidatedStore);
    return computed(() => getStickifiedContextName(selectedMember.value));
  }

  if (!isConsolidatedPage) {
    return computed(() => productId);
  }

  const { selectedUpcId, upcData } = useUpc();
  const { selected2ndVariantName } = useSelected2ndVariant();
  const latchKeyProductIdsByGroup = getMemberDisplayGroupsMap(product);

  return computed(() => {
    const selectedLatchkeyGroup = latchKeyProductIdsByGroup[selected2ndVariantName.value] || [];
    const [selectedMemberIdFromSelectedLatchkey] = selectedLatchkeyGroup;
    if (selectedLatchkeyGroup.length === 1 && selectedMemberIdFromSelectedLatchkey) {
      return selectedMemberIdFromSelectedLatchkey;
    }
    const selectedMemberIdFromSelectedUpc = upcData.value.upcs[selectedUpcId.value]?.relationships?.selectedProduct;

    return selectedMemberIdFromSelectedUpc || selectedMemberIdFromSelectedLatchkey || productId;
  });
}
